import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  PageHeader,
  Spinner,
  useToast,
} from "../../../design-system";
import { Icon } from "../../../design-system/Icon";
import {
  getNotificationPreferences,
  resetNotificationPreference,
  setNotificationPreference,
} from "../../../helpers/kronaly_helper";

const CATEGORY_META = {
  absence: { label: "Ausencias", icon: "CalendarDays" },
  document: { label: "Documentos", icon: "FileText" },
  overtime: { label: "Horas extra", icon: "Clock" },
  other: { label: "Otros", icon: "Bell" },
};

const unwrap = (r) => {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
};

export default function NotificacionesPreferenciasPage() {
  document.title = "Preferencias de notificaciones | Kronaly";
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingType, setSavingType] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getNotificationPreferences();
      setItems(unwrap(r) || []);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar las preferencias.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (item, field) => {
    const nextValue = !item[field];
    // Optimistic update: actualizamos local antes de pegar al backend.
    setItems((prev) =>
      prev.map((it) => (it.type === item.type ? { ...it, [field]: nextValue } : it)),
    );
    setSavingType(item.type);
    try {
      const inApp = field === "in_app" ? nextValue : item.in_app;
      const email = field === "email" ? nextValue : item.email;
      await setNotificationPreference(item.type, inApp, email);
    } catch (e) {
      // Revertimos optimistic en caso de fallo.
      setItems((prev) =>
        prev.map((it) => (it.type === item.type ? { ...it, [field]: !nextValue } : it)),
      );
      toast.error(
        "No se pudo guardar",
        typeof e === "string" ? e : e?.message || "Error desconocido",
      );
    } finally {
      setSavingType(null);
    }
  };

  const resetAll = async () => {
    setSavingType("__reset_all__");
    try {
      // Iterar y resetear cada uno que tenga override (in_app=false o email=false).
      const overrides = items.filter((it) => !it.in_app || !it.email);
      for (const it of overrides) {
        await resetNotificationPreference(it.type);
      }
      await load();
      toast.success("Preferencias restablecidas", "Todas las notificaciones vuelven a los valores por defecto.");
    } catch (e) {
      toast.error(
        "No se pudo restablecer",
        typeof e === "string" ? e : e?.message || "Error desconocido",
      );
    } finally {
      setSavingType(null);
    }
  };

  const hasOverrides = items.some((it) => !it.in_app || !it.email);

  // Agrupar por categoría.
  const groups = {};
  for (const item of items) {
    const cat = item.category || "other";
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(item);
  }
  const categoryOrder = ["absence", "document", "overtime", "other"];

  return (
    <div className="kr-page">
      <PageHeader
        eyebrow="Notificaciones"
        title="Preferencias"
        description="Decide qué notificaciones quieres recibir, y por qué canal. Aplica solo a este workspace."
        actions={
          hasOverrides && (
            <Button variant="ghost" icon="RotateCcw" onClick={resetAll} disabled={savingType !== null}>
              Restablecer todas
            </Button>
          )
        }
      />

      {error && <Alert tone="danger">{error}</Alert>}

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <Alert tone="info">No hay tipos de notificación definidos.</Alert>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {categoryOrder.map((cat) => {
            const rows = groups[cat];
            if (!rows || rows.length === 0) return null;
            const meta = CATEGORY_META[cat] || CATEGORY_META.other;
            return (
              <Card key={cat} style={{ padding: 0, overflow: "hidden" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "12px 20px",
                    borderBottom: "1px solid var(--kr-border-subtle)",
                    background: "var(--kr-bg-subtle)",
                  }}
                >
                  <Icon name={meta.icon} size={16} style={{ color: "var(--kr-fg-muted)" }} />
                  <span style={{ fontWeight: 600, color: "var(--kr-fg-strong)" }}>{meta.label}</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 100px", gap: 12, padding: "10px 20px", borderBottom: "1px solid var(--kr-border-subtle)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--kr-fg-muted)", fontWeight: 600 }}>
                  <span>Notificación</span>
                  <span style={{ textAlign: "center" }}>En la app</span>
                  <span style={{ textAlign: "center" }}>Por email</span>
                </div>
                {rows.map((item, idx) => {
                  const isOverride = !item.in_app || !item.email;
                  const isLast = idx === rows.length - 1;
                  return (
                    <div
                      key={item.type}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 100px 100px",
                        gap: 12,
                        alignItems: "center",
                        padding: "12px 20px",
                        borderBottom: isLast ? "none" : "1px solid var(--kr-border-subtle)",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                        <span
                          style={{
                            fontSize: 13,
                            color: "var(--kr-fg-strong)",
                            fontWeight: 500,
                          }}
                        >
                          {item.label}
                        </span>
                        {isOverride && <Badge tone="primary">Override</Badge>}
                      </div>
                      <div style={{ display: "flex", justifyContent: "center" }}>
                        <SwitchInline
                          value={item.in_app}
                          onChange={() => toggle(item, "in_app")}
                          disabled={savingType === item.type}
                          label="En la app"
                        />
                      </div>
                      <div style={{ display: "flex", justifyContent: "center" }}>
                        <SwitchInline
                          value={item.email}
                          onChange={() => toggle(item, "email")}
                          disabled={savingType === item.type}
                          label="Por email"
                        />
                      </div>
                    </div>
                  );
                })}
              </Card>
            );
          })}
          <Alert tone="info">
            <strong>Apagar ambos canales</strong> para un tipo te desactivará esa notificación
            completamente: ni la verás en la app ni te llegará por email.
          </Alert>
        </div>
      )}
    </div>
  );
}

/**
 * Switch on/off accesible. Sin primitive Switch en el DS — la mantengo
 * local porque es muy específica de este formulario.
 */
function SwitchInline({ value, onChange, disabled, label }) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      aria-label={`${label}: ${value ? "activado" : "desactivado"}`}
      aria-pressed={value}
      style={{
        position: "relative",
        width: 36,
        height: 20,
        borderRadius: 999,
        background: value ? "var(--kr-action)" : "var(--kr-neutral-300)",
        border: "none",
        cursor: disabled ? "wait" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "background var(--kr-dur-fast) var(--kr-ease-out)",
        padding: 0,
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          left: value ? 18 : 2,
          width: 16,
          height: 16,
          borderRadius: 999,
          background: "#fff",
          boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
          transition: "left var(--kr-dur-fast) var(--kr-ease-out)",
        }}
      />
    </button>
  );
}
